import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { cn, MODULOS } from '../lib/utils';
import {
    ChevronRight, ChevronDown, MessageSquare, FileText,
    BookOpen, Target, ClipboardList, Lock, ArrowLeft
} from 'lucide-react';

// Color map per task
const TAREA_COLORS = {
    'tarea-1': { border: 'border-sky-200', bg: 'bg-sky-50/60', accent: 'text-sky-700', badge: 'bg-sky-100 text-sky-800', icon: 'bg-sky-100 text-sky-600' },
    'tarea-2': { border: 'border-amber-200', bg: 'bg-amber-50/60', accent: 'text-amber-700', badge: 'bg-amber-100 text-amber-800', icon: 'bg-amber-100 text-amber-600' },
    'tarea-3': { border: 'border-violet-200', bg: 'bg-violet-50/60', accent: 'text-violet-700', badge: 'bg-violet-100 text-violet-800', icon: 'bg-violet-100 text-violet-600' },
};

function IndicadorCard({ indicador, tareaId, navigate }) {
    const colors = TAREA_COLORS[tareaId] || TAREA_COLORS['tarea-1'];

    return (
        <div
            className="group rounded-lg border border-slate-200 bg-white p-4 transition-all hover:border-slate-300 hover:shadow-sm"
            data-testid={`indicador-card-${indicador.id}`}
        >
            <div className="flex items-start gap-3">
                <div className={cn('mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', colors.icon)}>
                    <Target className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-slate-900 leading-snug">{indicador.nombre}</h4>
                    <p className="mt-1.5 text-sm text-slate-500 leading-relaxed whitespace-pre-line">
                        {indicador.descripcion}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-8 gap-1.5 text-xs"
                            onClick={() => navigate(`/chat?tarea=${tareaId}&indicador=${indicador.id}`)}
                            data-testid={`chat-indicador-${indicador.id}`}
                        >
                            <MessageSquare className="h-3.5 w-3.5" />
                            Consultar IA
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-8 gap-1.5 text-xs"
                            onClick={() => navigate(`/borradores?tarea=${tareaId}&indicador=${indicador.id}`)}
                            data-testid={`borrador-indicador-${indicador.id}`}
                        >
                            <FileText className="h-3.5 w-3.5" />
                            Borrador
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function TareaExpandable({ tarea, navigate }) {
    const [expanded, setExpanded] = useState(false);
    const colors = TAREA_COLORS[tarea.id] || TAREA_COLORS['tarea-1'];
    const numIndicadores = tarea.indicadores.length;

    return (
        <Card
            className={cn('overflow-hidden border-2 transition-all', colors.border, expanded && colors.bg)}
            data-testid={`tarea-card-${tarea.id}`}
        >
            <button
                className="w-full text-left"
                onClick={() => setExpanded(!expanded)}
                data-testid={`tarea-toggle-${tarea.id}`}
            >
                <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 min-w-0">
                            <div className={cn('mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl', colors.icon)}>
                                <ClipboardList className="h-5 w-5" />
                            </div>
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Badge className={cn('text-xs font-semibold', colors.badge)}>{tarea.nombre}</Badge>
                                    <span className="text-xs text-slate-400">
                                        {numIndicadores} indicador{numIndicadores > 1 ? 'es' : ''}
                                    </span>
                                </div>
                                <CardTitle className="mt-1.5 font-heading text-base font-semibold text-slate-900 leading-snug">
                                    {tarea.titulo}
                                </CardTitle>
                            </div>
                        </div>
                        <div className="shrink-0 mt-1">
                            {expanded ? (
                                <ChevronDown className="h-5 w-5 text-slate-400" />
                            ) : (
                                <ChevronRight className="h-5 w-5 text-slate-400" />
                            )}
                        </div>
                    </div>
                </CardHeader>
            </button>

            {expanded && (
                <CardContent className="pt-0 pb-5">
                    <div className="space-y-3">
                        <p className={cn('text-sm font-medium', colors.accent)}>Indicadores de evaluación</p>
                        {tarea.indicadores.map((ind) => (
                            <IndicadorCard
                                key={ind.id}
                                indicador={ind}
                                tareaId={tarea.id}
                                navigate={navigate}
                            />
                        ))}
                    </div>
                </CardContent>
            )}
        </Card>
    );
}

function ModuloView({ modulo, navigate }) {
    if (modulo.estado === 'proximo') {
        return (
            <Card className="border-dashed border-slate-300 bg-slate-50/50 opacity-70" data-testid={`modulo-card-${modulo.id}`}>
                <CardHeader>
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-200 text-slate-400">
                            <Lock className="h-5 w-5" />
                        </div>
                        <div>
                            <Badge variant="outline" className="text-xs text-slate-400 border-slate-300">{modulo.nombre}</Badge>
                            <CardTitle className="mt-1 font-heading text-base text-slate-400">{modulo.titulo}</CardTitle>
                            <CardDescription className="text-slate-400">Próximamente</CardDescription>
                        </div>
                    </div>
                </CardHeader>
            </Card>
        );
    }

    return (
        <div className="space-y-4" data-testid={`modulo-section-${modulo.id}`}>
            <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900 text-white">
                    <BookOpen className="h-5 w-5" />
                </div>
                <div>
                    <Badge className="bg-slate-900 text-white text-xs">{modulo.nombre}</Badge>
                    <h2 className="mt-0.5 font-heading text-lg font-bold text-slate-900">{modulo.titulo}</h2>
                </div>
            </div>
            <div className="space-y-4">
                {modulo.tareas.map((tarea) => (
                    <TareaExpandable key={tarea.id} tarea={tarea} navigate={navigate} />
                ))}
            </div>
        </div>
    );
}

// Detail view when accessing /tareas/:tareaId
function TareaDetail({ tareaId, navigate }) {
    const modulo = MODULOS.find((m) => m.tareas.some((t) => t.id === tareaId));
    const tarea = modulo?.tareas.find((t) => t.id === tareaId);

    if (!tarea) {
        return (
            <Layout>
                <div className="flex h-96 flex-col items-center justify-center gap-4">
                    <p className="text-slate-500">Tarea no encontrada</p>
                    <Button variant="outline" onClick={() => navigate('/tareas')}>Volver a Tareas</Button>
                </div>
            </Layout>
        );
    }

    const colors = TAREA_COLORS[tarea.id] || TAREA_COLORS['tarea-1'];

    return (
        <Layout>
            <div className="mx-auto max-w-3xl space-y-6 animate-fade-in">
                {/* Breadcrumb */}
                <div className="flex items-center gap-2 text-sm text-slate-500">
                    <button onClick={() => navigate('/tareas')} className="flex items-center gap-1 hover:text-slate-900 transition-colors">
                        <ArrowLeft className="h-4 w-4" />
                        Tareas
                    </button>
                    <ChevronRight className="h-4 w-4" />
                    <span className="text-slate-900">{tarea.nombre}</span>
                </div>

                {/* Header */}
                <div className={cn('rounded-xl border-2 p-6', colors.border, colors.bg)}>
                    <div className="flex items-start gap-4">
                        <div className={cn('flex h-12 w-12 shrink-0 items-center justify-center rounded-xl', colors.icon)}>
                            <ClipboardList className="h-6 w-6" />
                        </div>
                        <div>
                            <Badge className={cn('text-xs font-semibold', colors.badge)}>{tarea.nombre}</Badge>
                            <h1 className="mt-2 font-heading text-xl font-bold text-slate-900 leading-snug sm:text-2xl">
                                {tarea.titulo}
                            </h1>
                            <p className="mt-1 text-sm text-slate-500">
                                {tarea.indicadores.length} indicador{tarea.indicadores.length > 1 ? 'es' : ''} de evaluación
                            </p>
                        </div>
                    </div>
                </div>

                {/* Indicadores */}
                <div className="space-y-3">
                    <h3 className="font-heading font-semibold text-slate-900">Indicadores de evaluación</h3>
                    {tarea.indicadores.map((ind) => (
                        <IndicadorCard key={ind.id} indicador={ind} tareaId={tarea.id} navigate={navigate} />
                    ))}
                </div>
            </div>
        </Layout>
    );
}

export default function Tareas() {
    const { tareaId } = useParams();
    const navigate = useNavigate();

    if (tareaId) {
        return <TareaDetail tareaId={tareaId} navigate={navigate} />;
    }

    return (
        <Layout>
            <div className="mx-auto max-w-3xl space-y-8 animate-fade-in">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-slate-900 md:text-3xl">
                        Tareas del Portafolio
                    </h1>
                    <p className="mt-1 text-slate-500">
                        Explora cada módulo, tarea e indicador de tu evaluación docente
                    </p>
                </div>

                {MODULOS.map((modulo) => (
                    <ModuloView key={modulo.id} modulo={modulo} navigate={navigate} />
                ))}

                {/* How it works */}
                <Card className="border-slate-200 bg-slate-50/50">
                    <CardContent className="p-6">
                        <h3 className="font-heading font-semibold text-slate-900">
                            ¿Cómo trabajar cada tarea?
                        </h3>
                        <ul className="mt-3 space-y-2 text-sm text-slate-600">
                            <li className="flex items-start gap-2">
                                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-slate-400 shrink-0" />
                                Expande cada tarea para ver sus indicadores de evaluación
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-slate-400 shrink-0" />
                                Usa "Consultar IA" para recibir orientación específica del asistente
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-slate-400 shrink-0" />
                                Crea borradores y recibe retroalimentación para cada indicador
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-slate-400 shrink-0" />
                                Revisa tu progreso en el panel de avance
                            </li>
                        </ul>
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
}
